import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ChatInterface } from "./ChatInterface";
import { type RuntimeHealth, describeEnvironmentBanner } from "./runtimeModeViewModel";
import { AppSessionError, type AppUser, createAppSession } from "./appSession";
import { type HandoffSession, createHandoffSession, handoffIdFrom, handoffOrigins } from "./appHandoff.ts";
import { chatConfig } from "./runtimeConfig.ts";
import {
  conversationOwner,
  dropsChallenge,
  keepsConversation,
  planCompanySwitch,
  singleFlight,
  verdictEffect,
} from "./liveChatModel";
import { type ButtonKind, postActionButton } from "./payablesActionViewModel";

/**
 * Story SF-CHAT-6.5 — o frontend de teste em modo live.
 *
 * Login no app principal (e-mail e senha; código do autenticador quando o app
 * pede), sessão de chat criada com um grant, e um grant novo **a cada envio**.
 * Senha e código vão direto para a API do app; o backend do chat só vê grants.
 */

type Company = { id: number; nome: string };
// `empresa`: a empresa da conversa, para o operador (login sem empresa) voltar a
// ela ao entrar de novo sem perder a conversa (revisão da 8.2).
type ChatSession = { sessionId: string; correlationId: string; ownerKey: string; empresa: Company | null };

function sessionStorageOrMemory(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

async function createChatSession(grant: string): Promise<{ sessionId: string; correlationId: string }> {
  let resposta: Response;
  try {
    resposta = await fetch("/api/session/create", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ grant }),
    });
  } catch {
    throw new Error("O backend do chat não respondeu. Verifique se ele está no ar e tente de novo.");
  }
  const corpo = (await resposta.json().catch(() => null)) as
    | { session_id?: unknown; correlation_id?: unknown; detail?: unknown }
    | null;
  if (resposta.status === 503) {
    throw new Error("O chat está sem configuração para o modo live (ponte de contexto ou banco). Veja o /health do backend.");
  }
  if (!resposta.ok || typeof corpo?.session_id !== "string" || typeof corpo?.correlation_id !== "string") {
    throw new Error("O chat não conseguiu abrir a conversa com a sua autorização. Entre de novo.");
  }
  return { sessionId: corpo.session_id, correlationId: corpo.correlation_id };
}

interface LoginPanelProps {
  title: string;
  intro: string;
  notice?: string | null;
  onSignedIn: (user: AppUser) => void;
  app: ReturnType<typeof createAppSession>;
}

function LoginPanel({ title, intro, notice, onSignedIn, app }: LoginPanelProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (challenge ? codeRef : emailRef).current?.focus();
  }, [challenge]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const result = challenge
        ? await app.verifySecondFactor(challenge, code)
        : await app.signIn(email, password);
      if (result.kind === "needs_2fa") {
        setChallenge(result.challenge);
        setPassword("");
        return;
      }
      setPassword("");
      setCode("");
      onSignedIn(result.user);
    } catch (erro) {
      const mensagem = erro instanceof Error ? erro.message : "Não foi possível entrar.";
      if (dropsChallenge(erro instanceof AppSessionError ? erro.kind : null, Boolean(challenge))) {
        // O desafio morreu no app (vencido ou tentativas esgotadas): volta
        // para e-mail e senha, com o motivo à vista.
        setChallenge(null);
        setCode("");
      }
      setError(mensagem);
    } finally {
      setPending(false);
    }
  };

  const errorId = "login-error";
  const field =
    "mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 " +
    "placeholder:text-slate-500 focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-700/30 " +
    "aria-[invalid=true]:border-red-600";

  return (
    <section aria-labelledby="login-title" className="mx-auto w-full max-w-md rounded-lg border border-slate-300 bg-white p-6 shadow-sm">
      <h1 id="login-title" className="text-xl font-semibold text-slate-950 text-balance">{title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{intro}</p>
      {notice && (
        <p role="status" className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {notice}
        </p>
      )}
      <form className="mt-5 space-y-4" onSubmit={submit} noValidate>
        {!challenge ? (
          <>
            <label className="block text-sm font-medium text-slate-900">
              E-mail
              <input
                ref={emailRef}
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                className={field}
              />
            </label>
            <label className="block text-sm font-medium text-slate-900">
              Senha
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                className={field}
              />
            </label>
          </>
        ) : (
          <label className="block text-sm font-medium text-slate-900">
            Código do app autenticador
            <span className="mt-0.5 block text-sm font-normal text-slate-700">
              Os 6 dígitos que aparecem agora no app. O código muda a cada 30 segundos.
            </span>
            <input
              ref={codeRef}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]*"
              maxLength={7}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              className={`${field} tracking-[0.3em] tabular-nums`}
            />
          </label>
        )}
        {error && (
          <p id={errorId} role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
            {error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={pending}
            className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {pending ? (challenge ? "Conferindo código…" : "Entrando…") : challenge ? "Confirmar código" : "Entrar"}
          </button>
          {challenge && (
            <button
              type="button"
              onClick={() => {
                setChallenge(null);
                setCode("");
                setError(null);
              }}
              className="rounded px-2 py-1 text-sm font-medium text-slate-800 underline underline-offset-2 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
            >
              Voltar e usar outra conta
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/**
 * Story SF-CHAT-8.2: operador sem empresa vinculada escolhe a empresa antes do
 * grant. A lista vem do app; o app continua decidindo o acesso no grant.
 */
function CompanyPicker({
  companies,
  onChoose,
  title = "Escolha a empresa desta conversa",
  intro = "Sua conta acessa mais de uma empresa. O chat vai consultar e agir só sobre a que você escolher.",
  submitLabel = "Abrir a conversa",
  onCancel,
  busy = false,
  error = null,
}: {
  companies: { id: number; nome: string }[];
  onChoose: (empresa: { id: number; nome: string }) => void;
  title?: string;
  intro?: string;
  submitLabel?: string;
  /** Story SF-CHAT-9.5: na troca, voltar à conversa atual sem trocar. */
  onCancel?: () => void;
  /** Revisão da 9.5: enquanto o app confirma o grant da empresa escolhida. */
  busy?: boolean;
  error?: string | null;
}) {
  const [escolhida, setEscolhida] = useState<string>(companies[0] ? String(companies[0].id) : "");
  const tituloRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    // Na troca o seletor aparece por cima de uma conversa: o foco vai para ele.
    if (onCancel) tituloRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <section aria-labelledby="empresa-title" className="mx-auto w-full max-w-md rounded-lg border border-slate-300 bg-white p-6 shadow-sm">
      <h1 id="empresa-title" ref={tituloRef} tabIndex={-1} className="text-xl font-semibold text-slate-950 text-balance focus:outline-none">{title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-700">{intro}</p>
      <form
        className="mt-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          const empresa = companies.find((c) => String(c.id) === escolhida);
          if (empresa) onChoose(empresa);
        }}
      >
        <label className="block text-sm font-medium text-slate-900">
          Empresa
          <select
            value={escolhida}
            onChange={(e) => setEscolhida(e.target.value)}
            className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-700/30"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </label>
        {error && (
          <p role="alert" className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">{error}</p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="min-h-[44px] rounded-md bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
          >
            {busy ? "Confirmando acesso…" : submitLabel}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="min-h-[44px] rounded-md px-3 py-2 text-sm font-medium text-slate-800 underline underline-offset-2 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
            >
              Continuar nesta empresa
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/**
 * Story SF-CHAT-9.6: aberto pelo app-v2, não há login no chat. Quando a aba do
 * app some (fechada, logout), a pessoa volta ao app — nunca digita senha aqui.
 */
function HandoffEnded({ notice }: { notice: string | null }) {
  return (
    <section aria-labelledby="handoff-fim" className="mx-auto w-full max-w-md rounded-lg border border-slate-300 bg-white p-6 shadow-sm">
      <h1 id="handoff-fim" className="text-xl font-semibold text-slate-950 text-balance">Volte ao aplicativo para continuar</h1>
      <p role="status" className="mt-2 text-sm leading-relaxed text-slate-700">
        {notice ?? "Este assistente foi aberto pelo aplicativo. Para entrar de novo, abra o Assistente pelo aplicativo."}
      </p>
    </section>
  );
}

/**
 * Story SF-CHAT-9.6: `?entrada=app` com a aba do app aberta. Espera o olá do
 * app por postMessage; sem ele, oferece o login normal.
 */
export function HandoffEntry({ runtime, appBaseUrl }: { runtime: RuntimeHealth | null; appBaseUrl: string | undefined }) {
  // A sessão nasce e morre no efeito: no StrictMode a primeira montagem é
  // desfeita e o ouvinte dela sai junto (antes, um useMemo deixava um ouvinte vivo).
  const [sessao, setSessao] = useState<HandoffSession | null>(null);
  const [estado, setEstado] = useState<"esperando" | "pronto" | "falhou" | "login">("esperando");
  const [motivo, setMotivo] = useState<string | null>(null);
  const tentativaRef = useRef<HandoffSession | null>(null);
  useEffect(() => {
    const s = createHandoffSession({
      win: window,
      opener: window.opener as Window,
      handoffId: handoffIdFrom(window.location.hash) ?? "",
      allowedOrigins: handoffOrigins({
        VITE_APP_ORIGINS: chatConfig().VITE_APP_ORIGINS,
        VITE_APP_API_BASE_URL: appBaseUrl,
      }),
    });
    tentativaRef.current = s;
    let vivo = true;
    s.connect()
      .then(() => {
        if (!vivo) return;
        setSessao(s);
        setEstado("pronto");
      })
      .catch((erro) => {
        if (!vivo) return;
        setMotivo(erro instanceof Error ? erro.message : null);
        setEstado("falhou");
      });
    return () => {
      vivo = false;
      s.dispose();
    };
  }, [appBaseUrl]);

  if (estado === "pronto" && sessao) return <LiveChat runtime={runtime} appBaseUrl={appBaseUrl} handoff={sessao} />;
  if (estado === "login") return <LiveChat runtime={runtime} appBaseUrl={appBaseUrl} />;
  return (
    <main className="min-h-screen bg-slate-50 p-4 text-slate-900">
      {estado === "esperando" ? (
        <p role="status" aria-live="polite" className="mx-auto max-w-md">Recebendo a autorização do aplicativo…</p>
      ) : (
        <section role="alert" className="mx-auto w-full max-w-md rounded-lg border border-slate-300 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-950 text-balance">O aplicativo não entregou a autorização</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{motivo}</p>
          <button
            type="button"
            onClick={() => {
              tentativaRef.current?.dispose(); // a tentativa que falhou também sai
              tentativaRef.current = null;
              window.history.replaceState(null, "", window.location.pathname);
              setEstado("login");
            }}
            className="mt-4 min-h-[44px] rounded-md px-3 py-2 text-sm font-medium text-slate-800 underline underline-offset-2 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
          >
            Entrar com e-mail e senha
          </button>
        </section>
      )}
    </main>
  );
}

export function LiveChat({
  runtime,
  appBaseUrl,
  handoff,
}: {
  runtime: RuntimeHealth | null;
  appBaseUrl: string | undefined;
  /** Story SF-CHAT-9.6: grants pedidos à aba do app que abriu o chat. */
  handoff?: HandoffSession;
}) {
  const app = useMemo(
    () => handoff ?? createAppSession({ baseUrl: appBaseUrl, storage: sessionStorageOrMemory() }),
    [appBaseUrl, handoff],
  );
  const [user, setUser] = useState<AppUser | null>(() => app.current()?.user ?? null);
  const [chat, setChat] = useState<ChatSession | null>(null);
  const [opening, setOpening] = useState(false);
  const [fatal, setFatal] = useState<string | null>(null);
  const [reauth, setReauth] = useState<string | null>(null);
  const [turnError, setTurnError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<{ id: number; nome: string }[] | null>(null);
  // Story SF-CHAT-9.5: troca de empresa só pela UI (seletor → grant novo → conversa nova).
  const [switchList, setSwitchList] = useState<Company[] | null>(null);
  const [canSwitch, setCanSwitch] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [turnBusy, setTurnBusy] = useState(false);
  const conversaRef = useRef<HTMLDivElement>(null);
  const banner = describeEnvironmentBanner(runtime);

  // StrictMode roda efeitos duas vezes em dev: sem a trava, recarregar a aba
  // gastava dois grants e abria duas sessões de chat.
  const openChat = useMemo(
    () => singleFlight(async (signedIn: AppUser) => {
      setOpening(true);
      setFatal(null);
      try {
        const grant = await app.issueGrant();
        const criada = await createChatSession(grant);
        // Saiu enquanto abria: a sessão criada não pertence a ninguém na tela.
        if (!app.current()) return;
        setChat({
          ...criada,
          ownerKey: conversationOwner(signedIn),
          empresa: signedIn.empresaId !== null ? { id: signedIn.empresaId, nome: signedIn.empresaNome ?? "" } : null,
        });
      } catch (erro) {
        if (erro instanceof AppSessionError && erro.kind === "reauth") {
          setUser(null);
          setReauth(erro.message);
        } else {
          setFatal(erro instanceof Error ? erro.message : "Não foi possível abrir a conversa.");
        }
      } finally {
        setOpening(false);
      }
    }),
    [app],
  );

  // Login recuperado do sessionStorage (recarregar a aba) também abre a conversa.
  // Sem empresa (operador), primeiro a escolha; com uma só, ela é escolhida.
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);
  const loadCompanies = useCallback(() => {
    app
      .listCompanies()
      .then((lista) => {
        if (!vivoRef.current) return;
        if (lista.length === 1) {
          setUser(app.chooseCompany(lista[0]));
        } else if (lista.length === 0) {
          setFatal("O aplicativo não mostrou nenhuma empresa para a sua conta. Confira seu acesso no aplicativo.");
        } else {
          setCompanies(lista);
        }
      })
      .catch((erro) => {
        if (!vivoRef.current) return;
        if (erro instanceof AppSessionError && erro.kind === "reauth") {
          setUser(null);
          setReauth(erro.message);
        } else {
          setFatal(erro instanceof Error ? erro.message : "Não foi possível listar as empresas.");
        }
      });
  }, [app]);

  useEffect(() => {
    if (!user || chat || opening || fatal || reauth) return;
    if (user.empresaId !== null) {
      void openChat(user);
      return;
    }
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const signedIn = (entrou: AppUser) => {
    // Operador entrando de novo na mesma conversa: reaplica a empresa dela.
    const novo =
      entrou.empresaId === null && chat?.empresa && chat.ownerKey === `${entrou.id}:${chat.empresa.id}`
        ? app.chooseCompany(chat.empresa)
        : entrou;
    setReauth(null);
    setTurnError(null);
    setUser(novo);
    // Mesma pessoa e mesma empresa: a conversa continua. Outra: conversa nova,
    // porque a sessão do chat pertence a quem a abriu.
    if (!chat || !keepsConversation(chat.ownerKey, novo)) {
      setChat(null);
      // Sem empresa (operador), o efeito de `user` pede a escolha antes.
      if (novo.empresaId !== null) void openChat(novo);
    }
  };

  const signOut = () => {
    app.signOut();
    setUser(null);
    setChat(null);
    setReauth(null);
    setTurnError(null);
    setCompanies(null);
    setSwitchList(null);
    setCanSwitch(false);
  };

  // Story SF-CHAT-9.5: o botão do cabeçalho só aparece com mais de uma empresa.
  useEffect(() => {
    if (!chat) return;
    let vivo = true;
    app
      .listCompanies()
      .then((lista) => {
        if (vivo) setCanSwitch(planCompanySwitch(lista, chat.empresa?.id ?? null).kind === "pick");
      })
      .catch(() => {
        if (vivo) setCanSwitch(false);
      });
    return () => {
      vivo = false;
    };
  }, [app, chat]);

  const startSwitch = useCallback(() => {
    // Revisão da 9.5: não troca no meio de um turno.
    if (turnBusy) return;
    app
      .listCompanies()
      .then((lista) => {
        if (!vivoRef.current) return;
        const plano = planCompanySwitch(lista, user?.empresaId ?? null);
        if (plano.kind === "only_one") {
          setTurnError(
            handoff
              ? "A empresa desta conversa é a escolhida no aplicativo. Para trocar, troque lá e abra o Assistente de novo."
              : "Sua conta acessa só a empresa desta conversa.",
          );
        } else {
          setTurnError(null);
          setSwitchError(null);
          setSwitchList(plano.companies);
        }
      })
      .catch((erro) => {
        if (!vivoRef.current) return;
        if (erro instanceof AppSessionError && erro.kind === "reauth") {
          setUser(null);
          setReauth(erro.message);
        } else {
          setTurnError(erro instanceof Error ? erro.message : "Não foi possível listar as empresas.");
        }
      });
  }, [app, user, turnBusy, handoff]);

  // Revisão da 9.5: a empresa nova só fica gravada depois que o app emite o grant
  // e o chat abre a sessão. Recusa (403) ou falha voltam à empresa anterior, e a
  // conversa atual continua na tela.
  const switchTo = useCallback(
    async (empresa: Company) => {
      if (!chat || switching) return;
      const anterior = chat.empresa ?? (user?.empresaId != null ? { id: user.empresaId, nome: user.empresaNome ?? "" } : null);
      setSwitching(true);
      setSwitchError(null);
      const novo = app.chooseCompany(empresa);
      try {
        const grant = await app.issueGrant();
        const criada = await createChatSession(grant);
        if (!app.current()) return;
        setTurnError(null);
        setSwitchList(null);
        setUser(novo);
        setChat({ ...criada, ownerKey: conversationOwner(novo), empresa });
      } catch (erro) {
        if (anterior && app.current()) setUser(app.chooseCompany(anterior));
        if (erro instanceof AppSessionError && erro.kind === "reauth") {
          setSwitchList(null);
          setUser(null);
          setReauth(erro.message);
        } else {
          setSwitchError(erro instanceof Error ? erro.message : "Não foi possível trocar de empresa.");
        }
      } finally {
        setSwitching(false);
      }
    },
    [app, chat, switching, user],
  );

  const cancelSwitch = useCallback(() => {
    setSwitchList(null);
    setSwitchError(null);
    conversaRef.current?.focus();
  }, []);

  const requestTurnGrant = useCallback(async () => {
    try {
      return await app.issueGrant();
    } catch (erro) {
      if (erro instanceof AppSessionError && erro.kind === "reauth") {
        setUser(null);
        setReauth(erro.message);
      }
      throw erro;
    }
  }, [app]);

  const onTurnVerdict = useCallback(
    (status: string) => {
      const efeito = verdictEffect(status);
      if (efeito.signOut) {
        app.signOut();
        setUser(null);
        setReauth(efeito.notice);
      } else if (efeito.notice) {
        setTurnError(efeito.notice);
      }
      return efeito.restoreInput;
    },
    [app],
  );

  // Story SF-CHAT-8.2: cada botão do cartão pede um grant novo e chama a rota
  // do botão. O veredito passa pela mesma regra dos turnos (login de novo etc.).
  const onAction = useCallback(
    async (kind: ButtonKind, previewId: string) => {
      if (!chat) throw new Error("A conversa não está aberta.");
      const r = await postActionButton({
        kind, previewId, sessionId: chat.sessionId, correlationId: chat.correlationId, requestTurnGrant,
      });
      const efeito = verdictEffect(r.result_status);
      if (efeito.signOut) {
        app.signOut();
        setUser(null);
        setReauth(efeito.notice);
      }
      return r;
    },
    [app, chat, requestTurnGrant],
  );

  const onTurnError = useCallback(
    (mensagem: string) => {
      // Quando o motivo é reautenticação, o painel de login já diz isso.
      if (!app.current()) return;
      setTurnError(mensagem);
    },
    [app],
  );

  const header = (
    <header
      role={banner.live ? "status" : undefined}
      className={`mx-auto mb-4 max-w-4xl rounded-lg border p-3 text-sm ${
        banner.live
          ? "border-red-400 bg-red-50 text-red-950"
          : banner.unavailable
          ? "border-slate-400 bg-slate-100 text-slate-900"
          : "border-amber-300 bg-amber-50 text-amber-950"
      }`}
    >
      <strong>{banner.title}</strong> {banner.body}
    </header>
  );

  if (fatal) {
    return (
      <main className="min-h-screen bg-slate-50 p-4 text-slate-900">
        {header}
        <div role="alert" className="mx-auto max-w-4xl rounded-lg border border-red-300 bg-red-50 p-4">
          <h1 className="text-lg font-semibold text-red-950">A conversa não abriu</h1>
          <p className="mt-2 text-red-900">{fatal}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                if (!user) return;
                setFatal(null);
                // Sem empresa, tentar de novo é listar de novo, não abrir sem empresa.
                if (user.empresaId === null) loadCompanies();
                else void openChat(user);
              }}
              className="rounded-md bg-red-800 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-800"
            >
              Tentar de novo
            </button>
            <button
              type="button"
              onClick={signOut}
              className="rounded px-2 py-1 text-sm font-medium text-red-900 underline underline-offset-2 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-800"
            >
              Sair
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (user && !chat && companies && user.empresaId === null) {
    return (
      <main className="min-h-screen bg-slate-50 p-4 text-slate-900">
        {header}
        <CompanyPicker
          companies={companies}
          onChoose={(empresa) => {
            setCompanies(null);
            setUser(app.chooseCompany(empresa));
          }}
        />
        <p className="mx-auto mt-3 max-w-md text-sm">
          <button
            type="button"
            onClick={signOut}
            className="rounded px-2 py-1 font-medium text-slate-800 underline underline-offset-2 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
          >
            Sair e usar outra conta
          </button>
        </p>
      </main>
    );
  }

  if (!user && !chat) {
    return (
      <main className="min-h-screen bg-slate-50 p-4 text-slate-900">
        {header}
        {handoff ? <HandoffEnded notice={reauth} /> : <LoginPanel
          app={app}
          title="Entre com a sua conta do Seu Financeiro"
          intro="O e-mail, a senha e o código vão direto para o aplicativo. O chat recebe só uma autorização curta, renovada a cada mensagem."
          notice={reauth}
          onSignedIn={signedIn}
        />}
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 p-4 text-slate-900">
      {header}
      {user && (
        <div className="mx-auto mb-3 flex max-w-4xl flex-wrap items-center justify-between gap-2 text-sm text-slate-800">
          <p className="min-w-0">
            Conectado como <strong className="font-semibold">{user.nome}</strong>
            {user.empresaNome && <> · <span className="break-words">{user.empresaNome}</span></>}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {canSwitch && chat && (
              <button
                type="button"
                onClick={startSwitch}
                disabled={turnBusy || switching || switchList !== null}
                className="rounded px-2 py-1 font-medium underline underline-offset-2 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:cursor-not-allowed disabled:text-slate-500 disabled:no-underline"
              >
                Trocar de empresa
              </button>
            )}
            <button
              type="button"
              onClick={signOut}
              className="rounded px-2 py-1 font-medium underline underline-offset-2 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
            >
              Sair
            </button>
          </div>
        </div>
      )}
      {!user && (
        <div className="mx-auto mb-4 max-w-4xl">
          {handoff ? <HandoffEnded notice={reauth} /> : <LoginPanel
            app={app}
            title="Entre de novo para continuar"
            intro="A conversa abaixo continua aqui. Depois de entrar, reenvie a última mensagem."
            notice={reauth}
            onSignedIn={signedIn}
          />}
        </div>
      )}
      {turnError && user && (
        <div role="alert" className="mx-auto mb-4 flex max-w-4xl items-start justify-between gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <p>{turnError}</p>
          <button
            type="button"
            onClick={() => {
              setTurnError(null);
              conversaRef.current?.focus();
            }}
            className="shrink-0 rounded px-2 py-1 font-medium underline underline-offset-2 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
          >
            Dispensar
          </button>
        </div>
      )}
      {opening && !chat && (
        <p role="status" aria-live="polite" className="mx-auto max-w-4xl text-slate-800">
          Abrindo a conversa com a sua autorização…
        </p>
      )}
      {user && chat && switchList && (
        // Revisão da 9.5: por cima da conversa, sem desmontá-la — cancelar volta a ela intacta.
        <div className="mx-auto mb-4 max-w-4xl">
          <CompanyPicker
            companies={switchList}
            title="Trocar de empresa"
            intro={`O chat consulta uma empresa por vez${user.empresaNome ? ` (agora: ${user.empresaNome})` : ""}. Ao trocar, começa uma conversa nova com a empresa escolhida; o aplicativo confirma o seu acesso antes.`}
            submitLabel="Trocar e abrir a conversa"
            onCancel={cancelSwitch}
            busy={switching}
            error={switchError}
            onChoose={(empresa) => void switchTo(empresa)}
          />
        </div>
      )}
      {chat && (
        <div ref={conversaRef} tabIndex={-1} className="mx-auto h-[70vh] min-h-[28rem] max-w-4xl">
          <ChatInterface
            key={chat.sessionId}
            sessionId={chat.sessionId}
            correlationId={chat.correlationId}
            onTurnError={onTurnError}
            allowFileUpload={false}
            requestTurnGrant={requestTurnGrant}
            onTurnVerdict={onTurnVerdict}
            onAction={onAction}
            onSwitchCompany={startSwitch}
            onBusyChange={setTurnBusy}
            footerText="Modo live • dados do aplicativo • ações só com o botão Confirmar"
          />
        </div>
      )}
    </main>
  );
}
